import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useDeviceMutation } from '@/hooks/useDeviceMutation';
import { SectionHeader } from './SectionHeader';
import type { DeviceStatus, RelayMapEntry, ModuleEntry } from '@/lib/types';

interface Props { device: DeviceStatus; disabled: boolean }

const GPIO_MAP = [32, 33, 25, 26, 27, 14, 12, 13];
const MAX_RELAYS = 50;

interface DraftRelay { name: string; enabled: boolean }

function findModule(relayNum: number, modules: ModuleEntry[]): ModuleEntry | null {
  return modules.find((m) => relayNum >= m.startRelay && relayNum <= m.startRelay + m.channels - 1) ?? null;
}

export function RelayMapSection({ device, disabled }: Props) {
  const initialTotal = device.relayMap?.totalRelays ?? device.totalRelays ?? 8;
  const [total, setTotal] = useState<number>(initialTotal);
  const [rows, setRows]   = useState<DraftRelay[]>(() => {
    const arr: DraftRelay[] = [];
    for (let i = 0; i < initialTotal; i++) {
      const found = device.relayMap?.relays?.find((r) => r.idx === i);
      const fallback = device.relays?.[i];
      arr.push({
        name: found?.name ?? fallback?.name ?? `Relay ${i + 1}`,
        enabled: (found?.enabled ?? fallback?.enabled) !== false,
      });
    }
    return arr;
  });

  // Re-seed when device's relayMap echoes back changed values
  useEffect(() => {
    if (!device.relayMap) return;
    const t = device.relayMap.totalRelays;
    const arr: DraftRelay[] = [];
    for (let i = 0; i < t; i++) {
      const r = device.relayMap.relays.find((x) => x.idx === i);
      arr.push({ name: r?.name ?? `Relay ${i + 1}`, enabled: r?.enabled !== false });
    }
    setTotal(t);
    setRows(arr);
  }, [device.relayMap]);

  const modules: ModuleEntry[] = device.modules?.modules ?? [];

  const applyTotal = () => {
    setRows((r) => {
      if (total <= r.length) return r.slice(0, total);
      const extras: DraftRelay[] = [];
      for (let i = r.length; i < total; i++) extras.push({ name: `Relay ${i + 1}`, enabled: true });
      return [...r, ...extras];
    });
  };

  const updateRow = (i: number, patch: Partial<DraftRelay>) => {
    setRows((r) => r.map((row, idx) => idx === i ? { ...row, ...patch } : row));
  };

  const mut = useDeviceMutation<{ totalRelays: number; relays: RelayMapEntry[] }>(
    device.client_id, 'relay-map', { successToast: 'Relay map saved' },
  );

  const submit = () => {
    const relays: RelayMapEntry[] = [];
    for (let i = 0; i < total; i++) {
      const r = rows[i] ?? { name: `Relay ${i + 1}`, enabled: true };
      const isGpio = i < 8;
      const obj: RelayMapEntry = {
        idx: i,
        type: isGpio ? 'gpio' : 'modbus',
        name: r.name, enabled: r.enabled,
      };
      if (isGpio) obj.gpio = GPIO_MAP[i];
      relays.push(obj);
    }
    mut.mutate({ totalRelays: total, relays });
  };

  return (
    <div className="space-y-5">
      <SectionHeader title="Relay map"
        description="Name each relay and choose how many are active. Relays 1-8 use the on-board GPIOs; 9+ are routed to your Modbus modules." />

      <div className="flex items-center gap-3">
        <span className="text-xs font-semibold text-slate-700">Total relays</span>
        <Input type="number" min={1} max={MAX_RELAYS} value={total} disabled={disabled}
          className="!w-24"
          onChange={(e) => setTotal(Math.min(MAX_RELAYS, Math.max(1, parseInt(e.target.value || '1', 10))))} />
        <Button variant="outline" size="sm" onClick={applyTotal} disabled={disabled}>Apply</Button>
        <span className="text-xs text-slate-500 ml-auto">Showing {rows.length} of {total}</span>
      </div>

      <div className="overflow-x-auto rounded-lg border border-slate-200 max-h-[420px] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500 sticky top-0">
            <tr>
              <th className="px-3 py-2 text-left font-semibold">#</th>
              <th className="px-3 py-2 text-left font-semibold">Enable</th>
              <th className="px-3 py-2 text-left font-semibold">Name</th>
              <th className="px-3 py-2 text-left font-semibold">Type</th>
              <th className="px-3 py-2 text-left font-semibold">GPIO / Module</th>
              <th className="px-3 py-2 text-left font-semibold">Coil</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const isGpio = i < 8;
              const mod = isGpio ? null : findModule(i + 1, modules);
              const target = isGpio
                ? `GPIO ${GPIO_MAP[i]}`
                : mod ? `Module ${mod.id} (slave ${mod.slaveId})` : 'Unassigned';
              const coil  = isGpio ? '—' : mod ? String(i + 1 - mod.startRelay) : '?';
              return (
                <tr key={i} className="border-t border-slate-100">
                  <td className="px-3 py-1.5 text-xs tabular-nums text-slate-500">{i + 1}</td>
                  <td className="px-3 py-1.5">
                    <input type="checkbox" checked={r.enabled} disabled={disabled}
                      onChange={(e) => updateRow(i, { enabled: e.target.checked })}
                      className="w-4 h-4 accent-ink" />
                  </td>
                  <td className="px-3 py-1.5">
                    <Input value={r.name} maxLength={24} disabled={disabled}
                      className="!h-8 !text-xs"
                      onChange={(e) => updateRow(i, { name: e.target.value })} />
                  </td>
                  <td className="px-3 py-1.5 text-xs">
                    <span className={
                      'inline-flex px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ' +
                      (isGpio ? 'bg-sky-100 text-sky-700' : 'bg-violet-100 text-violet-700')
                    }>{isGpio ? 'GPIO' : 'Modbus'}</span>
                  </td>
                  <td className="px-3 py-1.5 text-xs">
                    {mod || isGpio ? <span className="text-slate-600">{target}</span>
                                   : <span className="text-red-600">Unassigned</span>}
                  </td>
                  <td className="px-3 py-1.5 text-xs tabular-nums text-slate-500">{coil}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Button onClick={submit} loading={mut.isPending} disabled={disabled || mut.isPending}>
        Save relay map
      </Button>
    </div>
  );
}
